import { useState, useEffect } from "react";
import axios from "axios";
import "@/App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation
} from "react-router-dom";
import HomePage from "@/pages/HomePage";

// Setup global axios interceptor to include auth token in all requests
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);
import StateBasedHomePage from "@/pages/StateBasedHomePage";
import ExtractPage from "@/pages/ExtractPage";
import SignaturePage from "@/pages/SignaturePage";
import DownloadPage from "@/pages/DownloadPage";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import InstitutionChecklistStep from "@/pages/InstitutionChecklistStep";
import EditDocumentPage from "@/pages/EditDocumentPage";
import EditOpinionFieldsPage from "@/pages/EditOpinionFieldsPage";
import ExtractionReviewPage from "@/pages/ExtractionReviewPage";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminPanel from "@/pages/admin/AdminPanel";
import TenantManagement from "@/pages/admin/TenantManagement";
import UserManagement from "@/pages/admin/UserManagement";
import BillingDashboard from "@/pages/admin/BillingDashboard";
import BrandingManagement from "@/pages/admin/BrandingManagement";
import DomainManagement from "@/pages/admin/DomainManagement";
import ReviewDashboard from "@/pages/ReviewDashboard";
import MyOpinionsPage from "@/pages/MyOpinionsPage";
import SharePage from "@/pages/SharePage";
import PasswordManagement from "@/pages/admin/PasswordManagement";
import FeatureToggleManagement from "@/pages/admin/FeatureToggleManagement";
import TenantStateManagement from "@/pages/admin/TenantStateManagement";
import ECUploadPage from "@/pages/ECUploadPage";
import ECDisplayPage from "@/pages/ECDisplayPage";
import SurveySelectionPage from "@/pages/SurveySelectionPage";
import TemplateManagement from "@/pages/TemplateManagement";
import DocumentToTemplateConverter from "@/pages/DocumentToTemplateConverter";
import WalletTransactionsPage from "@/pages/WalletTransactionsPage";
import AdminWalletManagement from "@/pages/admin/AdminWalletManagement";
import AdminPricingConfig from "@/pages/admin/AdminPricingConfig";
import OpinionFormatPricing from "@/pages/admin/OpinionFormatPricing";
import TenantDataReset from "@/pages/admin/TenantDataReset";
import ModulePricingConfig from "@/pages/admin/ModulePricingConfig";
import ModuleWalletManagement from "@/pages/admin/ModuleWalletManagement";
import CompanyMasterSettings from "@/pages/admin/CompanyMasterSettings";
import BillingProfileSettings from "@/pages/BillingProfileSettings";
import InvoiceHistoryPage from "@/pages/InvoiceHistoryPage";
import TenantProfilePage from "@/pages/TenantProfilePage";
import TenantDashboard from "@/pages/TenantDashboard";
import TenantUserDashboard from "@/pages/TenantUserDashboard";
import SuperAdminDashboard from "@/pages/SuperAdminDashboard";
import AuditLogsPage from "@/pages/AuditLogsPage";
import CaseChat from "@/pages/CaseChat";
import ChatListPage from "@/pages/ChatListPage";
import JobDashboard from "@/pages/JobDashboard";
import MissingDocsChecker from "@/pages/MissingDocsChecker";
// CSDashboard archived - using CSReportGenerator instead
import CSReportGenerator from "@/pages/CSReportGenerator";
import LegalOpinionValidation from "@/pages/LegalOpinionValidation";
import CaseDetailPage from "@/pages/CaseDetailPage";
import CaseHistoryPage from "@/pages/CaseHistoryPage";
import AdvocateProfileSettings from "@/pages/AdvocateProfileSettings";
import FloatingChat from "@/components/FloatingChat";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Toaster } from "@/components/ui/sonner";
import { BrandingProvider } from "@/lib/BrandingContext";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Floating Chat Wrapper - only shows when authenticated
const FloatingChatWrapper = () => {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('token'));
  
  // Check auth on location change
  useEffect(() => {
    setIsAuthenticated(!!localStorage.getItem('token'));
  }, [location]);
  
  const isLoginPage = location.pathname === '/login';
  
  // Don't show on login page
  if (isLoginPage || !isAuthenticated) {
    return null;
  }
  
  return <FloatingChat />;
};

// Dashboard Route with role-based rendering
const DashboardRoute = () => {
  const [userLoaded, setUserLoaded] = useState(false);
  const [user, setUser] = useState({});
  
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    }
    setUserLoaded(true);
  }, []);
  
  if (!userLoaded) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    </div>;
  }
  
  // Use appropriate dashboard based on role
  if (user.role === 'super_admin') {
    return <SuperAdminDashboard />;
  } else if (user.role === 'tenant_admin') {
    return <TenantDashboard />;
  } else if (user.role === 'cs' || user.role === 'cs_approver') {
    // CS and CS Approver see only the CS Dashboard
    return <CSDashboard />;
  } else {
    // For advocate, initiator, approver, reviewer
    return <TenantUserDashboard />;
  }
};

function App() {
  return (
    <BrandingProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <DashboardRoute />
                </ProtectedRoute>
              } 
            />
          <Route 
            path="/institution-checklist" 
            element={
              <ProtectedRoute>
                <InstitutionChecklistStep />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/extract" 
            element={
              <ProtectedRoute>
                <StateBasedHomePage workflow="retail" />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/extract/:extractionId" 
            element={
              <ProtectedRoute>
                <ExtractPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/signature/:documentId" 
            element={
              <ProtectedRoute>
                <SignaturePage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/download/:documentId" 
            element={
              <ProtectedRoute>
                <DownloadPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/edit/:documentId" 
            element={
              <ProtectedRoute>
                <EditDocumentPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/edit-fields/:documentId" 
            element={
              <ProtectedRoute>
                <EditOpinionFieldsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/review/:extractionId" 
            element={
              <ProtectedRoute>
                <ExtractionReviewPage />
              </ProtectedRoute>
            } 
          />
          {/* APF Legal Opinion Routes - Using State Schemas */}
          {/* DEPRECATED: APF routes removed - unified flow uses format selection instead */}
          {/* Redirect old APF routes to home page */}
          <Route 
            path="/apf-opinion" 
            element={
              <ProtectedRoute>
                <Navigate to="/home" replace />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/apf-client-opinion" 
            element={
              <ProtectedRoute>
                <Navigate to="/home" replace />
              </ProtectedRoute>
            } 
          />
          
          {/* Template Management Route - REMOVED: Now using programmatic generators */}
          <Route 
            path="/document-to-template" 
            element={
              <ProtectedRoute>
                <DocumentToTemplateConverter />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin" 
            element={
              <ProtectedRoute>
                <AdminPanel />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/tenants" 
            element={
              <ProtectedRoute>
                <TenantManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/users" 
            element={
              <ProtectedRoute>
                <UserManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/billing" 
            element={
              <ProtectedRoute>
                <BillingDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/review" 
            element={
              <ProtectedRoute>
                <ReviewDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/my-opinions" 
            element={
              <ProtectedRoute>
                <MyOpinionsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/share/:token" 
            element={<SharePage />} 
          />
          <Route 
            path="/password-management" 
            element={
              <ProtectedRoute>
                <PasswordManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/feature-toggles" 
            element={
              <ProtectedRoute>
                <FeatureToggleManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/state-management" 
            element={
              <ProtectedRoute>
                <TenantStateManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/branding" 
            element={
              <ProtectedRoute>
                <BrandingManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/audit-logs" 
            element={
              <ProtectedRoute>
                <AuditLogsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/job-dashboard" 
            element={
              <ProtectedRoute>
                <JobDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/chats" 
            element={
              <ProtectedRoute>
                <ChatListPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/case-chat/:caseId" 
            element={
              <ProtectedRoute>
                <CaseChat />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/domains" 
            element={
              <ProtectedRoute>
                <DomainManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/ec-extractor" 
            element={
              <ProtectedRoute>
                <ECUploadPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/ec-survey-select" 
            element={
              <ProtectedRoute>
                <SurveySelectionPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/ec/select-survey/:ecId" 
            element={
              <ProtectedRoute>
                <SurveySelectionPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/ec-display/:ecId" 
            element={
              <ProtectedRoute>
                <ECDisplayPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/wallet/transactions" 
            element={
              <ProtectedRoute>
                <WalletTransactionsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/wallet/management" 
            element={
              <ProtectedRoute>
                <AdminWalletManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/wallet/pricing" 
            element={
              <ProtectedRoute>
                <AdminPricingConfig />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/format-pricing" 
            element={
              <ProtectedRoute>
                <OpinionFormatPricing />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/tenant-reset" 
            element={
              <ProtectedRoute>
                <TenantDataReset />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/module-pricing" 
            element={
              <ProtectedRoute>
                <ModulePricingConfig />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/module-wallets" 
            element={
              <ProtectedRoute>
                <ModuleWalletManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/company-settings" 
            element={
              <ProtectedRoute>
                <CompanyMasterSettings />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/my-opinions" 
            element={
              <ProtectedRoute>
                <MyOpinionsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/missing-docs" 
            element={
              <ProtectedRoute>
                <MissingDocsChecker />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/cs-dashboard" 
            element={
              <ProtectedRoute>
                <CSReportGenerator />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/cs-report" 
            element={
              <ProtectedRoute>
                <CSReportGenerator />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/legal-opinion-validation" 
            element={
              <ProtectedRoute>
                <LegalOpinionValidation />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/case/:caseId" 
            element={
              <ProtectedRoute>
                <CaseDetailPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/case-history" 
            element={
              <ProtectedRoute>
                <CaseHistoryPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/settings/advocate-profile" 
            element={
              <ProtectedRoute>
                <AdvocateProfileSettings />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/settings/billing-profile" 
            element={
              <ProtectedRoute>
                <BillingProfileSettings />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/invoices" 
            element={
              <ProtectedRoute>
                <InvoiceHistoryPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/tenant-profile" 
            element={
              <ProtectedRoute>
                <TenantProfilePage />
              </ProtectedRoute>
            } 
          />
        </Routes>
        {/* FloatingChat disabled for stability testing */}
        {/* <FloatingChatWrapper /> */}
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
    </BrandingProvider>
  );
}

export default App;